/* ============================================
   PUBLICATIONS PAGE
   The Creative Orchestra - GEMINI Framework
   Doctoral research publications
   ============================================ */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ExternalLink, 
  Download, 
  FileText, 
  Award,
  Globe,
  Calendar,
  BookOpen,
  ArrowRight,
} from 'lucide-react';
import { ScrollReveal } from '@components/common';
import publications from '@data/publications.json';
import { SITE_INFO } from '@utils/constants';

/**
 * Publications Page Component
 */
const PublicationsPage = () => {
  useEffect(() => {
    document.title = `Publications | ${SITE_INFO.title}`;
  }, []);

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <ScrollReveal>
          <header className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              {publications.title}
            </h1>
            <p className="text-xl text-indigo-400 mb-2">
              {publications.subtitle}
            </p>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Research publications from the GEMINI framework and its constituent systems
            </p>
          </header>
        </ScrollReveal>

        {/* Publications List */}
        <div className="space-y-8">
          {publications.publications.map((pub, index) => (
            <PublicationCard key={pub.id} publication={pub} index={index} />
          ))}
        </div>

        {/* CTA */}
        <ScrollReveal delay={0.3}>
          <div className="mt-16 text-center">
            <Link to="/novel">
              <motion.button
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full
                  bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(129, 140, 248, 0.5)' }}
                whileTap={{ scale: 0.95 }}
              >
                <BookOpen className="w-5 h-5" />
                Read the Story Behind GEMINI
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};

/**
 * Publication Card Component
 */
const PublicationCard = ({ publication, index }) => {
  return (
    <ScrollReveal delay={index * 0.1}>
      <motion.div
        className="p-6 md:p-8 rounded-2xl bg-white/5 border border-white/10
          hover:bg-white/8 hover:border-white/20 transition-all"
        whileHover={{ y: -4 }}
      >
        {/* System Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="flex-grow">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-white">
                {publication.system}
              </h2>
              {publication.papers.some(p => p.award) && (
                <Award className="w-5 h-5 text-yellow-400" />
              )}
            </div>
            <p className="text-gray-400 mb-3">
              {publication.fullName}
            </p>
            <div className="flex flex-wrap gap-2">
              {publication.website && (
                <a
                  href={publication.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full
                    bg-indigo-500/20 border border-indigo-500/30 text-indigo-300
                    hover:bg-indigo-500/30 transition-colors text-sm"
                >
                  <Globe className="w-3 h-3" />
                  Website
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {publication.website1 && (
                <a
                  href={publication.website1}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full
                    bg-indigo-500/20 border border-indigo-500/30 text-indigo-300
                    hover:bg-indigo-500/30 transition-colors text-sm"
                >
                  <Globe className="w-3 h-3" />
                  Website 1
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {publication.website2 && (
                <a
                  href={publication.website2}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full
                    bg-indigo-500/20 border border-indigo-500/30 text-indigo-300
                    hover:bg-indigo-500/30 transition-colors text-sm"
                >
                  <Globe className="w-3 h-3" />
                  Website 2
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {publication.download && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full
                  bg-purple-500/20 border border-purple-500/30 text-purple-300 text-sm">
                  <Download className="w-3 h-3" />
                  {publication.download}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Papers */}
        {publication.papers.length > 0 ? (
          <div className="space-y-4">
            {publication.papers.map((paper, paperIndex) => (
              <PaperItem key={paperIndex} paper={paper} />
            ))}
          </div>
        ) : (
          <p className="text-gray-500 italic">Publications coming soon</p>
        )}
      </motion.div>
    </ScrollReveal>
  );
};

/**
 * Individual Paper Item
 */
const PaperItem = ({ paper }) => {
  return (
    <motion.div
      className="pl-4 border-l-2 border-indigo-500/30"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
    >
      {/* Title */}
      <h3 className="text-lg font-semibold text-white mb-2">
        {paper.title}
      </h3>

      {/* Metadata */}
      <div className="flex flex-wrap gap-2 text-sm text-gray-400 mb-3">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {paper.year}
        </span>
        <span>•</span>
        <span>{paper.authors}</span>
      </div>

      {/* Venue */}
      <p className="text-gray-300 mb-2">
        <span className="italic">{paper.venue}</span>
        {paper.volume && <span className="text-gray-500"> • {paper.volume}</span>}
        {paper.pages && <span className="text-gray-500"> • {paper.pages}</span>}
        {paper.publisher && <span className="text-gray-500"> • {paper.publisher}</span>}
      </p>

      {/* Status/Award */}
      {paper.award && (
        <div className="flex items-center gap-2 mb-2">
          <Award className="w-4 h-4 text-yellow-400" />
          <span className="text-yellow-400 font-medium text-sm">
            {paper.award}
          </span>
        </div>
      )}
      {paper.status && (
        <span className="inline-block px-3 py-1 rounded-full bg-orange-500/20 
          border border-orange-500/30 text-orange-300 text-sm mb-2">
          {paper.status}
        </span>
      )}

      {/* Links */}
      <div className="flex flex-wrap gap-2 mt-3">
        {paper.preprint && (
          <a
            href={paper.preprint}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg
              bg-green-500/10 border border-green-500/20 text-green-300
              hover:bg-green-500/20 transition-colors text-xs"
          >
            <FileText className="w-3 h-3" />
            Preprint
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {paper.doi && (
          <a
            href={`https://doi.org/${paper.doi}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg
              bg-white/5 border border-white/10 text-gray-400
              hover:text-white hover:border-white/20 transition-colors text-xs"
          >
            <ExternalLink className="w-3 h-3" />
            DOI: {paper.doi}
          </a>
        )}
        {paper.pdfPath && (
          <a
            href={paper.pdfPath}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg
              bg-red-500/10 border border-red-500/20 text-red-300
              hover:bg-red-500/20 transition-colors text-xs"
          >
            <FileText className="w-3 h-3" />
            View PDF
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default PublicationsPage;

